import { useState, useMemo, useEffect } from 'react';
import { Utensils, Trophy, ChevronDown } from 'lucide-react';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import PlayerAvatar from '../ui/PlayerAvatar';
import Tooltip from '../ui/Tooltip';
import TimeRangeFilter from '../filters/TimeRangeFilter';
import PlayerFilter from '../filters/PlayerFilter';
import PlayerButton from '../common/PlayerButton';
import { filterMatchesByTimeRange } from '../../utils/filters/timeRangeFilter';
import { formatMatchDate } from '../../utils/dateFormatter';
import { TEAM_COLORS } from '../../constants/uiConstants';

const MatchesTab = ({ matches, players, setSelectedPlayer, setActiveTab }) => {
  // Filter state
  const [timeRange, setTimeRange] = useState('all');
  const [selectedPlayerFilter, setSelectedPlayerFilter] = useState('all');
  const [resultFilter, setResultFilter] = useState('all'); // all, wins, losses
  const [breadFilter, setBreadFilter] = useState(['all']); // multi-select array
  const [breadDropdownOpen, setBreadDropdownOpen] = useState(false);

  // Reset result filter when no player is selected
  useEffect(() => {
    if (selectedPlayerFilter === 'all') {
      setResultFilter('all');
    }
  }, [selectedPlayerFilter]);

  // Close bread dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        breadDropdownOpen &&
        !event.target.closest('.bread-filter-dropdown')
      ) {
        setBreadDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [breadDropdownOpen]);

  // Filter and limit matches
  const displayedMatches = useMemo(() => {
    let filtered = filterMatchesByTimeRange(matches, timeRange);

    // Filter by player if selected
    if (selectedPlayerFilter !== 'all') {
      filtered = filtered.filter(
        match =>
          match.winners.includes(selectedPlayerFilter) ||
          match.losers.includes(selectedPlayerFilter)
      );
    }

    // Filter by result (win/loss) for selected player
    if (resultFilter !== 'all' && selectedPlayerFilter !== 'all') {
      filtered = filtered.filter(match => {
        if (resultFilter === 'wins') {
          return match.winners.includes(selectedPlayerFilter);
        } else if (resultFilter === 'losses') {
          return match.losers.includes(selectedPlayerFilter);
        }
        return true;
      });
    }

    // Filter by bread type (multi-select)
    if (!breadFilter.includes('all') && breadFilter.length > 0) {
      filtered = filtered.filter(match => {
        const isBread = match.score.includes('-0');
        const isHalfBread = match.score.includes('-1');
        const isNone = !isBread && !isHalfBread;

        return (
          (breadFilter.includes('full') && isBread) ||
          (breadFilter.includes('half') && isHalfBread) ||
          (breadFilter.includes('none') && isNone)
        );
      });
    }

    // Sort by timestamp (latest first)
    filtered = filtered.sort((a, b) => {
      const dateA = new Date(a.timestamp || a.date);
      const dateB = new Date(b.timestamp || b.date);
      return dateB - dateA; // Newest first
    });

    // Only limit to 30 when no filters are applied
    const hasFilters =
      timeRange !== 'all' ||
      selectedPlayerFilter !== 'all' ||
      resultFilter !== 'all' ||
      !breadFilter.includes('all');
    return !hasFilters ? filtered.slice(0, 30) : filtered;
  }, [matches, timeRange, selectedPlayerFilter, resultFilter, breadFilter]);

  const breadOptions = [
    { value: 'all', label: 'All Matches', emoji: '' },
    { value: 'full', label: 'Full Slice', emoji: '🍞' },
    { value: 'half', label: 'Half Slice', emoji: '🥪' },
    { value: 'none', label: 'No Bread', emoji: '🚫' },
  ];

  const handleBreadFilterToggle = value => {
    if (value === 'all') {
      setBreadFilter(['all']);
    } else {
      const newFilter = breadFilter.includes('all') ? [] : [...breadFilter];
      if (newFilter.includes(value)) {
        const filtered = newFilter.filter(v => v !== value);
        setBreadFilter(filtered.length === 0 ? ['all'] : filtered);
      } else {
        setBreadFilter([...newFilter, value]);
      }
    }
  };

  const getBreadFilterLabel = () => {
    if (breadFilter.includes('all')) return 'All Matches';
    if (breadFilter.length === 1) {
      const option = breadOptions.find(opt => opt.value === breadFilter[0]);
      return option ? `${option.emoji} ${option.label}` : 'Select...';
    }
    return `${breadFilter.length} types selected`;
  };

  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="pb-2">
        <div className="flex justify-between items-center mb-3">
          <div>
            <h2 className="text-lg font-bold tracking-tight text-zinc-900">
              Recent Activity
            </h2>
            {timeRange === 'all' &&
              selectedPlayerFilter === 'all' &&
              resultFilter === 'all' &&
              breadFilter.includes('all') && (
                <p className="text-xs text-zinc-500 mt-1">
                  Showing latest 30 matches
                </p>
              )}
            {(timeRange !== 'all' ||
              selectedPlayerFilter !== 'all' ||
              resultFilter !== 'all' ||
              !breadFilter.includes('all')) && (
              <p className="text-xs text-zinc-500 mt-1">
                Showing {displayedMatches.length} matches
                {selectedPlayerFilter !== 'all' && (
                  <span>
                    {' '}
                    for {players.find(p => p.id === selectedPlayerFilter)?.name}
                  </span>
                )}
                {resultFilter !== 'all' && <span> ({resultFilter})</span>}
                {!breadFilter.includes('all') && breadFilter.length > 0 && (
                  <span> ({breadFilter.join(', ')} slices)</span>
                )}
                {timeRange !== 'all' && <span> for selected period</span>}
              </p>
            )}
          </div>
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap gap-4 items-center">
          <PlayerFilter
            selectedPlayer={selectedPlayerFilter}
            setSelectedPlayer={setSelectedPlayerFilter}
            players={players}
          />

          {/* Result Filter - only show when player is selected */}
          {selectedPlayerFilter !== 'all' && (
            <div className="flex items-center gap-3">
              <Trophy className="w-4 h-4 text-zinc-400" />
              <select
                value={resultFilter}
                onChange={e => setResultFilter(e.target.value)}
                className="text-sm border border-zinc-200 rounded px-3 py-1.5 bg-white text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-400 focus:border-transparent"
              >
                <option value="all">All Results</option>
                <option value="wins">Wins Only</option>
                <option value="losses">Losses Only</option>
              </select>
            </div>
          )}

          {/* Bread Filter - Custom Multi-select Dropdown */}
          <div className="flex items-center gap-3">
            <Utensils className="w-4 h-4 text-zinc-400" />
            <div className="relative bread-filter-dropdown">
              <button
                onClick={() => setBreadDropdownOpen(!breadDropdownOpen)}
                className="text-sm border border-zinc-200 rounded px-3 py-1.5 bg-white text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-400 focus:border-transparent min-w-[160px] flex items-center justify-between"
              >
                <span className="truncate">{getBreadFilterLabel()}</span>
                <ChevronDown
                  className={`w-4 h-4 text-zinc-400 transition-transform ${breadDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {breadDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-zinc-200 rounded shadow-lg z-50">
                  {breadOptions.map(option => (
                    <div
                      key={option.value}
                      onClick={() => handleBreadFilterToggle(option.value)}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-zinc-50 flex items-center justify-between ${
                        breadFilter.includes(option.value)
                          ? 'bg-zinc-50 text-zinc-900'
                          : 'text-zinc-700'
                      }`}
                    >
                      <span>
                        {option.emoji} {option.label}
                      </span>
                      {breadFilter.includes(option.value) && (
                        <span className="text-zinc-400">✓</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <TimeRangeFilter
            timeRange={timeRange}
            setTimeRange={setTimeRange}
            matches={matches}
          />
        </div>
      </div>

      <div className="grid gap-3">
        {displayedMatches.map(match => {
          const winners = match.winners.map(
            id =>
              players.find(p => p.id === id) || {
                name: 'Unknown',
                initials: '?',
              }
          );
          const losers = match.losers.map(
            id =>
              players.find(p => p.id === id) || {
                name: 'Unknown',
                initials: '?',
              }
          );

          const isBread = match.score.includes('-0');
          const isHalfBread = match.score.includes('-1');

          return (
            <Card
              key={match.id}
              className="p-4 flex flex-col md:flex-row items-center justify-between gap-6 hover:border-zinc-300 transition-colors group"
            >
              <div className="flex items-center gap-4 min-w-[140px]">
                <div className="flex flex-col">
                  <span className="font-mono text-xs font-bold text-zinc-700">
                    {formatMatchDate(match.timestamp) || match.date}
                  </span>
                  {match.submittedByName && (
                    <Tooltip content={match.submittedByEmail || match.submittedByName} side="bottom">
                      <span className="text-[9px] text-zinc-500 tracking-wide font-bold">
                        Logged by {match.submittedByName}
                      </span>
                    </Tooltip>
                  )}
                </div>
              </div>

              <div className="flex-1 flex items-center justify-center gap-8 w-full">
                <div className="text-right flex-1">
                  <div className="font-bold text-sm text-zinc-900 truncate">
                    {winners.map((p, i) => (
                      <span key={p.id || i}>
                        <button
                          onClick={() => {
                            setSelectedPlayer(p);
                            setActiveTab('analytics');
                          }}
                          className="hover:text-indigo-600 transition-colors cursor-pointer font-bold text-sm bg-transparent border-none p-0 outline-none"
                        >
                          {p.name}
                        </button>
                        {i < winners.length - 1 && <span className="text-zinc-400"> & </span>}
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-1 justify-end mt-1.5">
                    {winners.map((p, i) => {
                      const isTeam1 = match.team1Players?.includes(p.id);
                      const teamColor = isTeam1 ? TEAM_COLORS.TEAM1 : TEAM_COLORS.TEAM2;
                      return (
                        <PlayerButton
                          key={p.id || `winner-${i}`}
                          player={p}
                          players={players}
                          setSelectedPlayer={setSelectedPlayer}
                          setActiveTab={setActiveTab}
                          className="hover:scale-110 transition-transform"
                          showAvatar={false}
                        >
                          <PlayerAvatar
                            initials={p.initials}
                            size="sm"
                            color={teamColor}
                          />
                        </PlayerButton>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col items-center gap-1">
                  <div className="font-mono text-xl font-bold tracking-widest text-zinc-900 px-6 py-2 bg-zinc-50 rounded border border-zinc-100">
                    {match.score}
                  </div>
                  {isBread && (
                    <Badge type="bread">
                      <Utensils className="w-3 h-3 text-red-500" /> Full Slice
                    </Badge>
                  )}
                  {isHalfBread && (
                    <Badge type="halfBread">
                      <Utensils className="w-3 h-3 text-amber-500" /> Half Slice
                    </Badge>
                  )}
                </div>

                <div className="text-left flex-1">
                  <div className="font-bold text-sm text-zinc-900 truncate">
                    {losers.map((p, i) => (
                      <span key={p.id || i}>
                        <button
                          onClick={() => {
                            setSelectedPlayer(p);
                            setActiveTab('analytics');
                          }}
                          className="hover:text-indigo-600 transition-colors cursor-pointer font-bold text-sm bg-transparent border-none p-0 outline-none"
                        >
                          {p.name}
                        </button>
                        {i < losers.length - 1 && <span className="text-zinc-400"> & </span>}
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-1 mt-1.5">
                    {losers.map((p, i) => {
                      const isTeam1 = match.team1Players?.includes(p.id);
                      const teamColor = isTeam1 ? TEAM_COLORS.TEAM1 : TEAM_COLORS.TEAM2;
                      return (
                        <PlayerButton
                          key={p.id || `loser-${i}`}
                          player={p}
                          players={players}
                          setSelectedPlayer={setSelectedPlayer}
                          setActiveTab={setActiveTab}
                          className="hover:scale-110 transition-transform"
                          showAvatar={false}
                        >
                          <PlayerAvatar
                            initials={p.initials}
                            size="sm"
                            color={teamColor}
                          />
                        </PlayerButton>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default MatchesTab;
